import { Injectable, NgZone } from '@angular/core';
import { map, retry, catchError } from 'rxjs/operators';
import { DataService } from './data.service';
import { AuthorizeRequest } from '../model/auth-req';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs/internal/Observable';
import { LoggingService } from './logging.service';
import { of, Subject, throwError } from 'rxjs';
import { User } from '../model/user';
import { CommonService } from './common.service';
import { messages } from '../resources/messages';
import { CookieService } from 'ngx-cookie-service';
import { Buffer } from 'buffer';

@Injectable({
  providedIn: 'root'
})

export class AuthService implements CanActivate {
  public static keepAliveTimer: NodeJS.Timer;
  private redirectUrl: string | undefined = '/';
  private static readonly loginUrl: string = '/authorization/failed';
  public static readonly userSessionKey: string = 'xbcs';
  public static readonly userDataKey: string = 'xbc-user-data';
  public static readonly userBookmarksKey: string = 'xbc-user-bookmarks';
  public msg = messages;
  public static readonly xbcSessionHeaderKey: string = 'xbc-session';

  constructor(
    private dataService: DataService, 
    private router: Router, 
    private loggingService: LoggingService,
    private commonService : CommonService,
    private cookieService: CookieService,
    private zone: NgZone
    ) { }

  login(xbcSession: string): Observable<any> {
    let subject = new Subject<any>();
    if (xbcSession.trim() != '') {
      const httpHeaders = new HttpHeaders({ 'xbc-session': xbcSession });

      this.dataService.postAuth('/auth/authorize/login', null, httpHeaders).subscribe(
        {
          next: response => {
            let xbcSession = response.headers.get(AuthService.xbcSessionHeaderKey);

            if (xbcSession != null && xbcSession.trim() != '') {
              this.onLoginSuccess(response, xbcSession);
              subject.next(response.body.data);
            }
            else {
              this.logSignInEvent(null, false);
              subject.next(false);
            }

          },
          error: error => {
            this.logSignInEvent(null, false);
            return subject.next(false);
          }
        }
      );
    }
    else {
      this.logSignInEvent(null, false);
      subject.next(false);
    }
    return subject.asObservable()
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    let userSession = this.cookieService.get(AuthService.userSessionKey)!;

    if (userSession != null && userSession.trim() != '') {
      const body: AuthorizeRequest = { token: state.url, fullResponse: false };
      const httpHeaders = new HttpHeaders({ 'xbc-session': userSession });

      return this.dataService.postAuth('/auth/authorize/authorize', body, httpHeaders).pipe(
        map(response => {
          if (response != null) {
            let xbcSession = response.headers.get(AuthService.xbcSessionHeaderKey);

            if (xbcSession != null && xbcSession.trim() != '') {
              if (userSession != xbcSession) {
                this.cookieService.set(AuthService.userSessionKey, xbcSession, { path: "/", secure: true, sameSite: 'Strict' });
              }
              if (response.body.data.isAuthorized) {
                return true;
              }
            }
          }
          this.zone.run(() => {
            this.router.navigate([this.getLoginUrl()]);
          });
          return false;
        }),
        catchError((e, caught) => {
          this.zone.run(() => {
            this.router.navigate([this.getLoginUrl()]);
          });
          return of(false);
        })
      );
    }
    this.zone.run(() => {
      this.router.navigate([this.getLoginUrl()]);
    });
    return false;
  }

  logoutUser(): Observable<any> {
    const logoutObserver = new Observable(observer => {
      let userSession = this.cookieService.get(AuthService.userSessionKey)!;
      if (userSession != null && userSession.trim() != '') {
        const httpHeaders = new HttpHeaders({ 'xbc-session': userSession });
        this.dataService.postAuth('/auth/authorize/logout', null, httpHeaders).subscribe(response => {
          this.loggingService.logEvent('User sign-out', { "ConnectId": localStorage.getItem("ConnectId"), "xbc-session": userSession, "logout_successful": response.body.data });
          observer.next()
          observer.complete();
        });
      }
    })
    return logoutObserver;
  }

  setRedirectUrl(url: string | undefined): void {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string | undefined {
    return this.redirectUrl;
  }

  getLoginUrl(): string {
    return AuthService.loginUrl;
  }

  public static clearKeepAliveInterval(): void {
    clearInterval(AuthService.keepAliveTimer)
  }

  keepAlive(): Observable<boolean> {
    let subject = new Subject<boolean>();
    let userSession = this.cookieService.get(AuthService.userSessionKey)!;
    if (userSession != null && userSession.trim() != '') {
      const httpHeaders = new HttpHeaders({ 'xbc-session': userSession });

      this.dataService.postAuth('/auth/authorize/keepalive', null, httpHeaders).pipe(
        retry(1),
        catchError((error: any) => {
          return throwError(() => subject.next(false));
        })
      ).subscribe(
        (data: any) => {
          if (data == null) {
            subject.next(false);
          }
          else if (data.body.data) {
            let xbcSession = data.headers.get(AuthService.xbcSessionHeaderKey) || "";
            if (xbcSession == "") {
              this.loggingService.logEvent('User session', { "ConnectId": localStorage.getItem("ConnectId"), "xbc-session": userSession, "keepAliveStatus": false });
              subject.next(false);
            }
            else if (userSession != xbcSession) {
              this.cookieService.set(AuthService.userSessionKey, xbcSession, { path: "/", secure: true, sameSite: 'Strict' });
              subject.next(true);
            }
            else {
              subject.next(true);
            }
          }
          else {
            this.loggingService.logEvent('User session', { "ConnectId": localStorage.getItem("ConnectId"), "xbc-session": userSession, "keepAliveStatus": false });
            subject.next(false);
          }
        }
      )
    }
    else {
      subject.next(false);
    }
    return subject.asObservable()
  }

  public clearStorageKeys() {
    localStorage.removeItem(AuthService.userBookmarksKey);
    this.cookieService.delete(AuthService.userDataKey);
  }

  private logSignInEvent(xbcSession: string | null, isLoginSuccessful: boolean): void {
    let connectId = localStorage.getItem("ConnectId");
    this.loggingService.logEvent('User sign-in', { "ConnectId": connectId, "xbc-session": xbcSession, "login_successful": isLoginSuccessful });
  }


  private onLoginSuccess(response: HttpResponse<any>, xbcSession: string) {
    this.setUserDataInCookies(response);
    localStorage.setItem(AuthService.userBookmarksKey, JSON.stringify(response.body.data.bookmarks));

    this.logSignInEvent(xbcSession, true);
  }

  private setUserDataInCookies(response: HttpResponse<any>) {
    let userData = new User();
    userData.xbcUserId = response.body.data.cashierId;
    userData.employeeId = response.body.data.employeeId;
    userData.emailId = response.body.data.email;
    let encodedData = Buffer.from(JSON.stringify(userData)).toString('base64');
    this.cookieService.set(AuthService.userDataKey, encodedData, { secure: true, path: "/", sameSite: 'Strict' });
  }

  isAuthorized(token: string, showErrorMsg: boolean = true): Observable<boolean> {
    let userSession = this.cookieService.get(AuthService.userSessionKey)!;
    const httpHeaders = new HttpHeaders({ 'xbc-session': userSession });
    const body: AuthorizeRequest = { token: token, fullResponse: false };

    return this.dataService.postAuth('/auth/authorize/authorize', body, httpHeaders).pipe(map(response => {
      if (response && response.body.data.isAuthorized) {
        return response.body.data.isAuthorized;
      }
      
      if (showErrorMsg) {
        const errorMsgObj = { msg: this.msg.CommonMessage.EntitlementInfoMessage, type: "error" };
        this.commonService.changeMessage(errorMsgObj);
      }
      return false;
    }))
  }

  isAuthorizedCheck(token: string): Observable<boolean> {
    let userSession = this.cookieService.get(AuthService.userSessionKey)!;
    const httpHeaders = new HttpHeaders({ 'xbc-session': userSession });
    const body: AuthorizeRequest = { token: token, fullResponse: false };

    return this.dataService.postAuth('/auth/authorize/authorize', body, httpHeaders).pipe(map(response => {
      if (response && response.body.data.isAuthorized) {
        return response.body.data.isAuthorized;
      }
      return false;
    }))
  }
}
